import React, { useRef, useState, useCallback, useEffect } from 'react';
import axiosInstance from '../services/axiosInstance';


const Form = ({ formState, setFormState, disabled }) => {
    const debounceTimer = useRef(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [channelSearch, setChannelSearch] = useState('');
    const abortControllerRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const fetchChannels = useCallback(async (buyerId, selectedButton) => {
        if (!buyerId || !selectedButton) {
            setFormState(prev => ({
                ...prev,
                params: {
                    ...prev.params,
                    channelIds: []
                }
            }));
            return;
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            const { data } = await axiosInstance.post(
                '/api/get_unique_channel_ids',
                { buyerId, selectedButton },
                { signal: controller.signal }
            );

            const sortedData = [...(data || [])].sort((a, b) => {
                const numA = Number(a);
                const numB = Number(b);
                const isNumA = a !== null && a !== '' && !isNaN(numA);
                const isNumB = b !== null && b !== '' && !isNaN(numB);
                if (isNumA && isNumB) {
                    return numA - numB;
                }
                return String(a).localeCompare(String(b));
            });

            const additionalValues = ["All channels separate", "All channels combined"];
            setFormState(prev => ({
                ...prev,
                params: {
                    ...prev.params,
                    channelIds: sortedData.length ? [...sortedData, ...additionalValues] : additionalValues
                },
                errors: {
                    ...prev.errors,
                    buyerId: ''
                }
            }));
        } catch (error) {
            if (error.name !== 'CanceledError') {
                console.error('Failed to fetch channels:', error);

                // Distinguish "server responded with an error" (e.g. the 404 the
                // backend sends when a buyer genuinely has no channels) from
                // "the request never reached the server" (network/CORS failure) -
                // these need different messages or a network outage reads as
                // "this buyer has no data", which is misleading.
                const backendErrorMsg = error.response
                    ? (error.response.data?.detail || 'No channels found for this Buyer ID')
                    : 'Could not reach the server. Please check your connection and try again.';

                setFormState(prev => ({
                    ...prev,
                    params: {
                        ...prev.params,
                        channelIds: [],
                        selectedChannelIds: [],
                        selectedChannels: [],
                        channels_with_names: []
                    },
                    errors: {
                        ...prev.errors,
                        buyerId: backendErrorMsg
                    }
                }));
            }
        }
    }, [setFormState]);

    const handleBuyerIdChange = useCallback((e) => {
        const value = e.target.value.trim();
        
        setChannelSearch(''); // Reset search when buyer ID changes

        setFormState(prev => ({
            ...prev,
            params: {
                ...prev.params,
                buyerId: value,
                channelIds: [],
                selectedChannelIds: [],
                selectedChannels: [],
                channels_with_names: []
            },
            errors: {
                ...prev.errors,
                buyerId: '',
                setSelectedButtons: value && prev.params.setSelectedButtons.length === 0
                    ? 'Please select Payday or Personal'
                    : ''
            }
        }));

        clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            if (value && formState.params.setSelectedButtons.length > 0) {
                fetchChannels(value, formState.params.setSelectedButtons[0]);
            }
        }, 500);
    }, [fetchChannels, formState.params.setSelectedButtons, setFormState]);

    const handleButtonClick = useCallback((buttonName, event) => {
        event.preventDefault();
        
        setChannelSearch(''); // Reset search when product type changes

        setFormState(prev => {
            const newSelectedButtons = prev.params.setSelectedButtons.includes(buttonName)
                ? []
                : [buttonName];

            return {
                ...prev,
                params: {
                    ...prev.params,
                    setSelectedButtons: newSelectedButtons,
                    channelIds: [],
                    selectedChannelIds: [],
                    selectedChannels: [],
                    channels_with_names: []
                },
                errors: {
                    ...prev.errors,
                    setSelectedButtons: ''
                }
            };
        });
    }, [setFormState]);

    const _buyerId = formState.params.buyerId;
    const _selectedButton = formState.params.setSelectedButtons[0];

    useEffect(() => {
        if (_buyerId && _selectedButton) {
            fetchChannels(_buyerId, _selectedButton);
        }
    }, [_buyerId, _selectedButton, fetchChannels]);

    const handleDateChange = (field) => (e) => {
        const dateValue = e.target.value ? new Date(e.target.value) : null;
        setFormState(prev => ({
            ...prev,
            params: {
                ...prev.params,
                [field]: dateValue
            },
            errors: {
                ...prev.errors,
                [field]: ''
            }
        }));
    };

    const toggleDropdown = () => {
        setIsDropdownOpen((prevState) => !prevState);
    };

    const handleChannelSelection = (id) => {
        const { selectedChannelIds, channelIds } = formState.params;
        const selectedValue = channelIds.find(channelId => channelId === id);

        // Handle "All channels..." options
        if (typeof selectedValue === 'string') {
            setFormState(prevState => {
                // Check if the option is already selected
                const isAlreadySelected = prevState.params.channels_with_names.some(
                    group => group.channels === selectedValue
                );

                if (isAlreadySelected) {
                    // If already selected, remove it
                    return {
                        ...prevState,
                        params: {
                            ...prevState.params,
                            channels_with_names: prevState.params.channels_with_names.filter(
                                group => group.channels !== selectedValue
                            )
                        }
                    };
                } else {
                    // If not selected, add it as a single entry
                    return {
                        ...prevState,
                        params: {
                            ...prevState.params,
                            channels_with_names: [
                                ...prevState.params.channels_with_names,
                                { channels: selectedValue, group_name: "" }
                            ]
                        }
                    };
                }
            });
            return;
        }

        // Handle numeric channel IDs
        if (selectedChannelIds.includes(id)) {
            setFormState(prevState => ({
                ...prevState,
                params: {
                    ...prevState.params,
                    selectedChannelIds: selectedChannelIds.filter(channelId => channelId !== id)
                }
            }));
        } else {
            setFormState(prevState => ({
                ...prevState,
                params: {
                    ...prevState.params,
                    selectedChannelIds: [...selectedChannelIds, id]
                }
            }));
        }
    };

    const handleGroupChannels = () => {
        const { selectedChannelIds } = formState.params;

        if (selectedChannelIds.length === 0) return;

        const newGroup = { channels: selectedChannelIds, group_name: '' };

        setFormState(prevState => ({
            ...prevState,
            params: {
                ...prevState.params,
                selectedChannels: [...prevState.params.selectedChannels, selectedChannelIds],
                selectedChannelIds: [],
                channels_with_names: [...prevState.params.channels_with_names, newGroup],
            },
        }));
    };

    const handleRemoveChannel = (channelList) => {
        setFormState(prev => ({
            ...prev,
            params: {
                ...prev.params,
                selectedChannels: prev.params.selectedChannels.filter(
                    selectedList => !arraysAreEqual(selectedList, channelList)
                ),
                channels_with_names: prev.params.channels_with_names.filter(
                    group => !arraysAreEqual(group.channels, channelList)
                ),
            },
        }));
    };

    const handleGroupNameChange = (channelList, e) => {
        const newGroupName = e.target.value;
        setFormState(prev => {
            const updatedGroups = prev.params.channels_with_names.map(group =>
                arraysAreEqual(group.channels, channelList)
                    ? { ...group, group_name: newGroupName }
                    : group
            );
            return {
                ...prev,
                params: {
                    ...prev.params,
                    channels_with_names: updatedGroups
                }
            };
        });
    };
    const formatDateForInput = (date) => {
        if (!date || !(date instanceof Date)) return '';
        return date.toISOString().split('T')[0];
    };

    const arraysAreEqual = (arr1, arr2) => {
        if (arr1.length !== arr2.length) return false;
        for (let i = 0; i < arr1.length; i++) {
            if (arr1[i] !== arr2[i]) return false;
        }
        return true;
    };

    useEffect(() => {
        return () => {
            clearTimeout(debounceTimer.current);
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    return (
        <form>
            <div className="form-section">

                {/* ━━━ SECTION 1 · DATA SOURCE ━━━ */}
                <div className="v2-section-label">① Data Source</div>

                {/* Product Type + Buyer ID */}
                <div className="form-section-1">
                    <div className="form-container-2">
                        <div className="toggle-container">
                            <button
                                onClick={(e) => handleButtonClick('Payday', e)}
                                className={`view-button ${formState.params.setSelectedButtons.includes('Payday') ? 'active' : ''}`}
                                disabled={disabled}
                            >
                                Payday
                            </button>
                            <button
                                onClick={(e) => handleButtonClick('Personal', e)}
                                className={`view-button ${formState.params.setSelectedButtons.includes('Personal') ? 'active' : ''}`}
                                disabled={disabled}
                            >
                                Personal
                            </button>
                        </div>
                        {formState.errors.setSelectedButtons && (
                            <p className="error-message error-message-p">
                                {formState.errors.setSelectedButtons}
                            </p>
                        )}
                    </div>
                    <div className="form-container-1">
                        <div className="form-group group-ids">
                            <label>BUYER_ID</label>
                            <input
                                type="text"
                                placeholder="BUYER_ID"
                                value={formState.params.buyerId || ''}
                                onChange={handleBuyerIdChange}
                                disabled={disabled}
                            />
                        </div>
                    </div>
                </div>

                {/* Buyer ID error — full width, own row, so a longer message
                    (e.g. a network failure) never overlaps the narrow input above it */}
                {formState.errors.buyerId && (
                    <div className="v2-full-row">
                        <p className="error-message error-message-p">{formState.errors.buyerId}</p>
                    </div>
                )}

                {/* Channel IDs — full width, no floating right */}
                {formState.params.buyerId && formState.params.channelIds.length > 0 && (
                    <div className="v2-full-row">
                        <div className="form-group group-ids">
                            <label>
                                CHANNEL_IDS
                                {formState.params.channels_with_names.length > 0 && (
                                    <span className="v2-count-badge">
                                        {formState.params.channels_with_names.length} group{formState.params.channels_with_names.length > 1 ? 's' : ''}
                                    </span>
                                )}
                            </label>
                            <div className="dropdown" ref={dropdownRef}>
                                <div
                                    className={`dropdown-toggle ${disabled ? 'disabled' : ''}`}
                                    onClick={() => !disabled && toggleDropdown()}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <button
                                            className="group-btn"
                                            onClick={(e) => { e.stopPropagation(); if (!disabled) handleGroupChannels(); }}
                                            type="button"
                                            disabled={disabled}
                                        >
                                            Group
                                        </button>
                                        <span className="dropdown-placeholder">
                                            {formState.params.selectedChannelIds.length > 0
                                                ? `${formState.params.selectedChannelIds.length} selected — click Group to confirm`
                                                : 'Select a CHANNEL_ID'}
                                        </span>
                                    </div>
                                    <span className="dropdown-arrow">▼</span>
                                </div>
                                {isDropdownOpen && !disabled && (
                                    <div className="dropdown-menu dropdown-menu-with-search">
                                        <div className="dropdown-search" style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                            <input
                                                type="text"
                                                placeholder="Search channel..."
                                                value={channelSearch}
                                                onChange={(e) => setChannelSearch(e.target.value)}
                                                style={{ width: '100%', padding: '6px', boxSizing: 'border-box' }}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                        <div className="dropdown-items-container">
                                            {formState.params.channelIds.filter(id => String(id).toLowerCase().includes(channelSearch.toLowerCase())).map((id, index) => {
                                                const isChecked = formState.params.selectedChannelIds.includes(id);
                                                return (
                                                    <div key={index} className={`dropdown-item ${isChecked ? "selected" : ""}`}>
                                                        <label>
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={() => !disabled && handleChannelSelection(id)}
                                                                disabled={disabled}
                                                            />
                                                            {id}
                                                        </label>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Grouped channel tags stacked below — full width */}
                            {formState.params.channels_with_names.length > 0 && (
                                <div className="selected-channel-ids" style={{ marginTop: '10px' }}>
                                    {formState.params.channels_with_names.map((group, index) => (
                                        <div key={index} className="channel-id-box">
                                            <span>
                                                {typeof group.channels === 'string'
                                                    ? group.channels
                                                    : group.channels.join(', ')}
                                            </span>
                                            {Array.isArray(group.channels) && group.channels.length >= 2 && (
                                                <input
                                                    type="text"
                                                    placeholder="Group Name"
                                                    className="group-name-input"
                                                    value={group.group_name}
                                                    onChange={(e) => handleGroupNameChange(group.channels, e)}
                                                    disabled={disabled}
                                                />
                                            )}
                                            <button
                                                type="button"
                                                className="remove-btn"
                                                onClick={() => handleRemoveChannel(group.channels)}
                                                disabled={disabled}
                                            >
                                                &#8722;
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Report Type — full width, own row */}
                <div className="v2-full-row">
                    <div className="form-group">
                        <label>Report Type</label>
                        <div className="segmented-control">
                            <button
                                type="button"
                                className={`segmented-option ${formState.params.reportType === 'bad' ? 'active' : ''}`}
                                onClick={() => setFormState(prev => ({
                                    ...prev,
                                    params: { ...prev.params, reportType: 'bad' }
                                }))}
                                disabled={disabled}
                            >
                                Bad Performing Sources
                            </button>
                            <button
                                type="button"
                                className={`segmented-option ${formState.params.reportType === 'best' ? 'active' : ''}`}
                                onClick={() => setFormState(prev => ({
                                    ...prev,
                                    params: { ...prev.params, reportType: 'best' }
                                }))}
                                disabled={disabled}
                            >
                                Best Performing Sources
                            </button>
                        </div>
                    </div>
                </div>

                <div className="v2-divider" />

                {/* ━━━ SECTION 2 · DATE RANGES ━━━ */}
                <div className="v2-section-label">② Date Ranges</div>

                <div className="form-section-3">
                    <div className="form-group-current">
                        <h2>Current Period</h2>
                        <div className="group-dates">
                            <div className="form-group group-date">
                                <label>Start Date</label>
                                <input
                                    type="date"
                                    value={formatDateForInput(formState.params.current_startDate)}
                                    onChange={handleDateChange('current_startDate')}
                                    disabled={disabled}
                                />
                                {formState.errors.current_startDate && (
                                    <p className="error-message">{formState.errors.current_startDate}</p>
                                )}
                            </div>
                            <div className="form-group group-date">
                                <label>End Date</label>
                                <input
                                    type="date"
                                    value={formatDateForInput(formState.params.current_endDate)}
                                    onChange={handleDateChange('current_endDate')}
                                    disabled={disabled}
                                />
                                {formState.errors.current_endDate && (
                                    <p className="error-message">{formState.errors.current_endDate}</p>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="form-group-previous">
                        <h2>Previous Period</h2>
                        <div className="group-dates">
                            <div className="form-group group-date">
                                <label>Start Date</label>
                                <input
                                    type="date"
                                    value={formatDateForInput(formState.params.previous_startDate)}
                                    onChange={handleDateChange('previous_startDate')}
                                    disabled={disabled}
                                />
                                {formState.errors.previous_startDate && (
                                    <p className="error-message">{formState.errors.previous_startDate}</p>
                                )}
                            </div>
                            <div className="form-group group-date">
                                <label>End Date</label>
                                <input
                                    type="date"
                                    value={formatDateForInput(formState.params.previous_endDate)}
                                    onChange={handleDateChange('previous_endDate')}
                                    disabled={disabled}
                                />
                                {formState.errors.previous_endDate && (
                                    <p className="error-message">{formState.errors.previous_endDate}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </form>
    );
};

export default Form;
